import type { Fixture, Round } from '@h2h/shared';

/** Round lifecycle label for headers (Compete, Fixtures, etc.). */
export function roundPhaseLabel(status: Round['status'] | undefined): string | null {
  switch (status) {
    case 'OPEN':
      return 'Cards open';
    case 'LOCKING':
      return 'Live';
    case 'SETTLING':
      return 'Settling';
    case 'SETTLED':
      return 'Final';
    case 'SCHEDULED':
      return 'Upcoming';
    default:
      return null;
  }
}

export type FixtureCardPhase = 'open' | 'saved' | 'missed' | 'locked' | 'live' | 'final' | 'void';

/** Where a fixture sits in the BYC lifecycle for list rows and badges. */
export function fixtureCardPhase(fixture: Fixture, hasCard: boolean, now: number): FixtureCardPhase {
  if (fixture.status === 'VOID') return 'void';
  if (fixture.status === 'SETTLED') return 'final';
  if (fixture.status === 'LIVE') return 'live';
  const started = fixture.status !== 'SCHEDULED' || now >= fixture.kickoffAt;
  if (started) return hasCard ? 'locked' : 'missed';
  if (hasCard) return 'saved';
  return 'open';
}

export const FIXTURE_BADGE: Record<FixtureCardPhase, string> = {
  open: 'Build card',
  saved: 'Card saved',
  missed: 'No card',
  locked: 'Locked in',
  live: 'Live',
  final: 'Final',
  void: 'Void'
};

/** Summarise a round slate for the Fixtures tab header. */
export function slateCardSummary(
  fixtures: Fixture[],
  cardIds: Set<string>,
  now: number
): { built: number; open: number; missed: number; live: number; final: number } {
  let built = 0;
  let open = 0;
  let missed = 0;
  let live = 0;
  let final = 0;
  for (const f of fixtures) {
    const hasCard = cardIds.has(f.fixtureId);
    const phase = fixtureCardPhase(f, hasCard, now);
    if (hasCard) built++;
    if (phase === 'open') open++;
    if (phase === 'missed') missed++;
    if (phase === 'live' || phase === 'locked') live++;
    if (phase === 'final') final++;
  }
  return { built, open, missed, live, final };
}
